import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { MONTHS, fmtSEK, g } from "../revenue/format";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../ui/sheet";

const LINE_META = {
  tm: { title: "Time & Material", link: { to: "/planning/resource-planning", label: "Open Resource Planning" } },
  projects: { title: "Projects", category: "PROJECT", link: { to: "/planning/revenue", label: "Open Revenue Planning" } },
  services: { title: "Services", category: "SERVICE", link: { to: "/planning/revenue", label: "Open Revenue Planning" } },
  other: { title: "Other Revenue", category: "OTHER", link: { to: "/planning/revenue", label: "Open Revenue Planning" } },
  employee_direct: { title: "Employee Consultants", link: { to: "/planning/personnel-costs", label: "Open Personnel Costs" } },
  subcontractor_direct: { title: "Subcontractors", link: { to: "/planning/resource-planning", label: "Open Resource Planning" } },
  regular_indirect: { title: "Regular Indirect Costs", link: { to: "/planning/indirect-costs", label: "Open Indirect Costs" } },
  overhead_personnel: { title: "Overhead Personnel Costs", link: { to: "/planning/personnel-costs", label: "Open Personnel Costs" } },
  factoring: { title: "Factoring (6064)", link: { to: "/planning/indirect-costs", label: "Open Indirect Costs" } },
  group_overhead: { title: "Group Overhead (6591)" },
  depreciation: { title: "Depreciation" },
  interest: { title: "Interest" },
  corporate_tax: { title: "Corporate Tax" },
  profit_after_tax: { title: "Profit After Tax" },
};

// Build 8.5 — which accounting classifications feed each P&L line.
const LINE_ACTUALS_FILTER = {
  tm: (r) => r.classification === "REVENUE" && r.sales_line === "tm",
  projects: (r) => r.classification === "REVENUE" && r.sales_line === "projects",
  services: (r) => r.classification === "REVENUE" && r.sales_line === "services",
  other: (r) => r.classification === "REVENUE" && (r.sales_line === "other" || !r.sales_line),
  employee_direct: (r) => r.classification === "PERSONNEL_COST",
  subcontractor_direct: (r) => r.classification === "SUBCONTRACTOR_DIRECT_COST",
  regular_indirect: (r) => r.classification === "REGULAR_INDIRECT_COST" || r.classification === "OTHER_UNCLASSIFIED",
  factoring: (r) => r.classification === "FACTORING",
  group_overhead: (r) => r.classification === "GROUP_OVERHEAD",
  depreciation: (r) => r.classification === "DEPRECIATION",
  interest: (r) => r.classification === "INTEREST",
};

function Row({ label, value, sub, testId }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-slate-100" data-testid={testId}>
      <div>
        <div className="text-sm text-slate-800">{label}</div>
        {sub && <div className="text-xs text-slate-400">{sub}</div>}
      </div>
      <div className="text-sm font-semibold text-slate-900">{value == null ? "—" : `${fmtSEK(value)} kr`}</div>
    </div>
  );
}

export default function PnlDrilldownPanel({ open, onClose, line, month, data, year }) {
  const [m, setM] = useState(month);
  useEffect(() => {
    setM(month);
  }, [month, line, open]);

  if (!data || !line || !LINE_META[line]) {
    return <Sheet open={open} onOpenChange={(v) => !v && onClose()} />;
  }
  const meta = LINE_META[line];
  const dd = data.drilldown;
  const cell = g(data.months, m) || {};
  const actual = dd.actuals?.[String(m)];

  let body = null;
  if (actual && LINE_ACTUALS_FILTER[line]) {
    // Build 8.5: committed accounting actuals are authoritative for this month —
    // show the account postings from the import instead of forecast drilldowns.
    const filter = LINE_ACTUALS_FILTER[line];
    const rows = (actual.rows || []).filter(filter);
    body = (
      <>
        <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2 mb-3" data-testid="pnl-dd-actuals-note">
          <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">
            Accounting actuals — committed import {actual.file_name}
            {actual.committed_at ? ` (${new Date(actual.committed_at).toLocaleString("sv-SE")})` : ""}.
            Costs are shown in P&L convention (positive).
          </p>
        </div>
        {rows.map((r, i) => (
          <Row
            key={i}
            label={`${r.account_number} ${r.description || ""}`}
            sub={r.classification === "OTHER_UNCLASSIFIED" ? "Other / Unclassified" : null}
            value={r.classification === "REVENUE" ? r.period_amount : -r.period_amount}
            testId={`pnl-dd-actual-${i}`}
          />
        ))}
        {rows.length === 0 && <p className="text-sm text-slate-500">No postings classified to this line.</p>}
      </>
    );
  } else if (line === "tm") {
    const entries = g(dd.tm, m) || [];
    body = entries.length === 0 ? (
      <p className="text-sm text-slate-500">No T&M assignments with revenue this month.</p>
    ) : (
      entries.map((e, i) => (
        <Row key={i} label={`${e.consultant_name} / ${e.assignment_name}`}
          sub={`${e.billable_hours ?? "—"} h × ${e.hourly_sales_rate ?? "—"} kr${e.actual_revenue != null ? " · actual" : ""}`}
          value={e.actual_revenue != null && cell.closed ? e.actual_revenue : e.forecast_revenue}
          testId={`pnl-dd-tm-${i}`} />
      ))
    );
  } else if (meta.category) {
    const rows = (dd.revenue_sources || []).filter((s) => s.category === meta.category);
    body = rows.length === 0 ? (
      <p className="text-sm text-slate-500">No {meta.title.toLowerCase()} sources.</p>
    ) : (
      rows.map((s, i) => <Row key={i} label={s.name} sub={s.customer_name} value={g(s.months, m)} testId={`pnl-dd-src-${i}`} />)
    );
  } else if (line === "employee_direct") {
    body = (dd.employees || []).map((e, i) => (
      <Row key={i} label={e.name} sub="Active monthly personnel cost (Build 4)" value={g(e.months, m)} testId={`pnl-dd-emp-${i}`} />
    ));
    if ((dd.employees || []).length === 0) body = <p className="text-sm text-slate-500">No consultant employees.</p>;
  } else if (line === "overhead_personnel") {
    body = (
      <>
        <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2 mb-3" data-testid="pnl-dd-ohp-note">
          <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">{dd.overhead_personnel?.note}</p>
        </div>
        {(dd.overhead_employees || []).map((e, i) => (
          <Row key={i} label={e.name} sub="Overhead employee — active monthly personnel cost" value={g(e.months, m)} testId={`pnl-dd-ohe-${i}`} />
        ))}
        {(dd.overhead_employees || []).length === 0 && <p className="text-sm text-slate-500">No overhead employees.</p>}
      </>
    );
  } else if (line === "subcontractor_direct") {
    body = (dd.subcontractors || []).map((s, i) => (
      <Row key={i} label={s.label} sub={s.customer_name} value={g(s.months, m)} testId={`pnl-dd-sub-${i}`} />
    ));
    if ((dd.subcontractors || []).length === 0) body = <p className="text-sm text-slate-500">No subcontractor assignments.</p>;
  } else if (line === "regular_indirect") {
    const rows = (dd.indirect_accounts || []).filter((a) => !a.system && !a.factoring);
    body = rows.map((a, i) => (
      <Row key={i} label={`${a.account_number} ${a.description || ""}`} value={g(a.months, m)} testId={`pnl-dd-acct-${i}`} />
    ));
    if (rows.length === 0) body = <p className="text-sm text-slate-500">No regular indirect cost accounts.</p>;
  } else if (line === "factoring") {
    const rows = (dd.indirect_accounts || []).filter((a) => a.factoring);
    body = (
      <>
        <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2 mb-3" data-testid="pnl-dd-factoring-note">
          <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">{dd.factoring.note || "Factoring is a revenue-related cost (account 6064), counted once in Gross Profit and never inside Regular Indirect Costs."}</p>
        </div>
        {rows.map((a, i) => <Row key={i} label={`${a.account_number} ${a.description || ""}`} value={g(a.months, m)} testId={`pnl-dd-fact-${i}`} />)}
        {rows.length === 0 && <p className="text-sm text-slate-500">No factoring account rows.</p>}
      </>
    );
  } else if (line === "group_overhead") {
    body = (
      <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2" data-testid="pnl-dd-go-note">
        <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">
          {dd.group_overhead.note} Current state: {dd.group_overhead.state === "calculated" ? "calculated values exist" : "not calculated — contributes 0 until Build 8"}.
        </p>
      </div>
    );
  } else if (line === "corporate_tax" || line === "profit_after_tax") {
    const t = data.fy.tax_detail || {};
    body = (
      <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2" data-testid="pnl-dd-tax-note">
        <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">
          Annual calculation (TAX-001/002): basis = Expected FY PBT {fmtSEK(t.basis)} kr × {t.rate == null ? "rate not configured" : `${t.rate}%`} = {t.suggested_tax == null ? "—" : `${fmtSEK(t.suggested_tax)} kr`}.
          Negative or zero results carry zero suggested tax. Cash payments are Finance planning inputs under Financials → Cash Flow.
        </p>
      </div>
    );
  } else {
    body = (
      <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2" data-testid="pnl-dd-dep-int-note">
        <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{dd.depreciation_interest.note}</p>
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto" data-testid="pnl-drilldown">
        <SheetHeader>
          <SheetTitle className="font-heading">{meta.title}</SheetTitle>
          <SheetDescription>
            {data.company_name} — {year}. Why is this number what it is?
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex flex-wrap gap-1" data-testid="pnl-dd-months">
          {MONTHS.map((label, i) => (
            <button
              key={i + 1}
              onClick={() => setM(i + 1)}
              className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                m === i + 1 ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`pnl-dd-month-${i + 1}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-3 text-xs text-slate-500" data-testid="pnl-dd-active-value">
          Active value {MONTHS[m - 1]}: <span className="font-semibold text-slate-900">{cell[line] == null ? "—" : `${fmtSEK(cell[line])} kr`}</span>
          <span className="ml-2">{cell.actual_source === "ACCOUNTING_IMPORT" ? "Accounting actuals · committed import" : cell.closed ? "Closed · actual" : "Open · forecast"}</span>
        </div>

        <div className="mt-3 border border-slate-200 rounded-lg p-3" data-testid="pnl-dd-breakdown">
          {body}
        </div>

        {meta.link && (
          <Link
            to={meta.link.to}
            className="inline-block mt-4 text-sm font-semibold text-[#0052CC] hover:underline"
            data-testid="pnl-dd-source-link"
          >
            {meta.link.label} →
          </Link>
        )}
      </SheetContent>
    </Sheet>
  );
}
